import React from 'react';
import { Image as ImageIcon, Save, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import FileUpload from '../../components/FileUpload';
import { UNIT_OPTIONS } from './catalogUnits';
import UnitConversionHint from './UnitConversionHint';

const ProductForm = ({
  editingProduct,
  productForm,
  setProductForm,
  categories,
  onSubmit,
  onCancel,
}) => (
  <Card className="mb-6" data-testid="product-form">
    <CardHeader>
      <CardTitle>{editingProduct ? 'Edit Product' : 'Add New Product'}</CardTitle>
    </CardHeader>
    <CardContent>
      <form onSubmit={onSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Name *</label>
            <Input
              value={productForm.name}
              onChange={(e) => setProductForm((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="Product name"
              required
              data-testid="product-name-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Price per sale unit (₦) *</label>
            <Input
              type="number"
              value={productForm.price}
              onChange={(e) => setProductForm((prev) => ({ ...prev, price: e.target.value }))}
              placeholder="0"
              required
              data-testid="product-price-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Category</label>
            <select
              value={productForm.category}
              onChange={(e) => {
                const category = categories.find((item) => item.slug === e.target.value);
                setProductForm((prev) => ({
                  ...prev,
                  category: e.target.value,
                  unit: category?.default_unit || prev.unit,
                  unit_value: category?.default_unit_value || prev.unit_value,
                }));
              }}
              className="w-full px-3 py-2 border rounded-md"
              data-testid="product-category-select"
            >
              <option value="">Select Category</option>
              {categories.map((cat) => (
                <option key={cat._id} value={cat.slug}>{cat.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Availability</label>
            <select
              value={productForm.availability}
              onChange={(e) => setProductForm((prev) => ({ ...prev, availability: e.target.value }))}
              className="w-full px-3 py-2 border rounded-md"
              data-testid="product-availability-select"
            >
              <option value="in_stock">Available</option>
              <option value="out_of_stock">Out of stock</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">MOQ (sale units)</label>
            <Input
              type="number"
              step="0.1"
              value={productForm.min_order_quantity}
              onChange={(e) => setProductForm((prev) => ({ ...prev, min_order_quantity: e.target.value }))}
              placeholder="1"
              data-testid="product-min-qty-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Selling unit</label>
            <select
              value={productForm.unit}
              onChange={(e) => setProductForm((prev) => ({ ...prev, unit: e.target.value }))}
              className="w-full px-3 py-2 border rounded-md"
              data-testid="product-unit-select"
            >
              {UNIT_OPTIONS.map((unit) => <option key={unit.value} value={unit.value}>{unit.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Unit value</label>
            <Input
              type="number"
              min="0.001"
              step="0.001"
              value={productForm.unit_value}
              onChange={(e) => setProductForm((prev) => ({ ...prev, unit_value: e.target.value }))}
              placeholder="1"
              data-testid="product-unit-value-input"
            />
          </div>
          <div className="flex items-end">
            <UnitConversionHint
              unit={productForm.unit}
              unitValue={productForm.unit_value}
              testId="product-unit-conversion-hint"
            />
          </div>
          <div className="md:col-span-2 lg:col-span-3">
            <label className="block text-sm font-medium mb-1">Description</label>
            <textarea
              value={productForm.description}
              onChange={(e) => setProductForm((prev) => ({ ...prev, description: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 border rounded-md"
              placeholder="Product description..."
              data-testid="product-description-input"
            />
          </div>
        </div>

        <div className="border-t pt-4">
          <label className="block text-sm font-medium mb-2">
            <ImageIcon className="inline h-4 w-4 mr-1" /> Product Images
          </label>
          <FileUpload
            category="products"
            files={productForm.images}
            onChange={(newImages) => setProductForm((prev) => ({ ...prev, images: newImages }))}
            multiple={true}
            showPrimary={true}
            sortable={true}
            label="Upload Images from Device"
          />
        </div>

        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={productForm.is_active}
              onChange={(e) => setProductForm((prev) => ({ ...prev, is_active: e.target.checked }))}
              className="rounded"
              data-testid="product-active-checkbox"
            />
            <span className="text-sm">Active (visible in shop)</span>
          </label>
        </div>

        <div className="flex gap-2">
          <Button type="submit" data-testid="save-product-btn">
            <Save className="h-4 w-4 mr-2" /> {editingProduct ? 'Update Product' : 'Create Product'}
          </Button>
          <Button type="button" variant="outline" onClick={onCancel} data-testid="cancel-product-btn">
            <X className="h-4 w-4 mr-2" /> Cancel
          </Button>
        </div>
      </form>
    </CardContent>
  </Card>
);

export default ProductForm;
